<template>
  <input
    ref="datePicker"
    class="form-input form-datepicker form-control form-input-bordered"
    :disabled="disabled"
    :class="{ 'cursor-not-allowed': disabled }"
    type="text"
    :placeholder="placeholder"
  >
</template>

<script>
  import flatpickr from 'flatpickr';
  import { German } from 'flatpickr/dist/l10n/de';
  import { Dutch } from 'flatpickr/dist/l10n/nl';
  import 'flatpickr/dist/themes/airbnb.css';
  import { useTimeZonedDates } from '@/ui/concerns/useTimeZonedDates';

  export default {
    props: {
      value: {
        type: String,
        default: null,
      },
      placeholder: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      enableTime: {
        type: Boolean,
        default: true,
      },
      enableDate: {
        type: Boolean,
        default: true,
      },
    },

    data: () => ({
      flatpickr: null,
      shouldOpenOnStart: false,
    }),

    computed: {
      dateType() {
        if (this.enableTime && this.enableDate) {
          return 'datetime';
        }
        if (this.enableDate) {
          return 'date';
        }
        if (this.enableTime) {
          return 'time';
        }
        return null;
      },
      outputFormat() {
        return {
          datetime: 'Y-m-d H:i',
          date: 'Y-m-d',
          time: 'H:i',
        }[this.dateType];
      },
      formatDate() {
        return {
          datetime: 'shortDateTime',
          date: 'shortDate',
          time: 'time',
        }[this.dateType];
      },
    },

    watch: {
      dateType: {
        immediate: true,
        handler() {
          if (this.flatpickr) {
            this.flatpickr.destroy();
          }
          this.buildPicker();
        },
      },
    },

    destroyed() {
      if (this.flatpickr) {
        this.flatpickr.destroy();
      }
    },

    methods: {
      async buildPicker() {
        const locale = {
          de: German,
          nl: Dutch,
        }[this.$i18n.locale] || null;

        await this.$nextTick();

        this.flatpickr = flatpickr(this.$refs.datePicker, {
          enableTime: this.enableTime,
          noCalendar: !this.enableDate,
          onClose: this.onChange,
          onChange: this.onChange,
          formatDate: dateTime => this.$d(useTimeZonedDates().getDateInLocalTimeZone(dateTime), this.formatDate),
          defaultDate: this.value,
          time_24hr: !this.twelveHourTime,
          locale,
        });

        if (this.shouldOpenOnStart) {
          this.flatpickr.open();
        }
      },
      onChange(selectedDates) {
        if (selectedDates.length === 1) {
          this.$emit('change', flatpickr.formatDate(selectedDates[0], this.outputFormat));
        } else {
          this.$emit('change', null);
        }
      },
      async redrawFromValue() {
        //be sure flatpickr was initialized
        await this.$nextTick();
        if (!this.value) {
          this.flatpickr.clear();
        } else {
          this.flatpickr.setDate(this.value, false);
        }
      },
    },
  };
</script>
